import { useState, useEffect } from "react";
import { getBanners, createBanner, updateBanner, deleteBanner, getAdminArticles, getCategories } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { 
  Megaphone, Plus, Trash2, Edit, X, Loader2, Check, Calendar, 
  Image, Eye, EyeOff, Upload, ChevronDown, ChevronUp, ExternalLink,
  Target, Users, Globe, BarChart3, Layout
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const bannerTypes = [
  { value: "partner", label: "Partenaire", description: "Pub partenaire ou sponsor" },
  { value: "deal", label: "Bon Plan", description: "Mise en avant d'un bon plan" },
  { value: "membership", label: "Adhésion", description: "Incitation à l'adhésion" },
  { value: "internal", label: "Autopromo", description: "Promo interne ITERCATUS" },
  { value: "editorial", label: "Éditorial", description: "Mise en avant d'article" },
];

const positionOptions = [
  { value: "sidebar", label: "Sidebar (flottante)", description: "Colonne de droite" },
  { value: "article_top", label: "Haut d'article", description: "Avant le contenu" },
  { value: "article_middle", label: "Milieu d'article", description: "Après 3 paragraphes" },
  { value: "article_bottom", label: "Bas d'article", description: "Après le contenu" },
  { value: "home_hero", label: "Accueil - Hero", description: "Sous le hero" },
  { value: "home_middle", label: "Accueil - Milieu", description: "Zone centrale" },
  { value: "footer", label: "Footer", description: "Pied de page" },
];

const visibilityOptions = [
  { value: "all", label: "Tous les visiteurs" },
  { value: "members_only", label: "Adhérents uniquement" },
  { value: "non_members", label: "Non-adhérents uniquement" },
];

const targetingOptions = [
  { value: "all", label: "Tous les contenus" },
  { value: "category", label: "Par catégorie" },
  { value: "specific", label: "Articles spécifiques" },
];

export default function BannersManagementPage() {
  const [banners, setBanners] = useState([]);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [expandedBanner, setExpandedBanner] = useState(null);
  
  // Filters
  const [filterPosition, setFilterPosition] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const [form, setForm] = useState({
    name: "",
    banner_type: "partner",
    position: "sidebar",
    image_url: "",
    image_base64: "",
    link_url: "",
    alt_text: "",
    is_active: true,
    order: 0,
    // Targeting
    targeting_type: "all",
    target_categories: [],
    target_article_ids: [],
    // Visibility
    visibility: "all",
    // Period
    start_date: "",
    end_date: "",
    // Stats
    clicks: 0,
    impressions: 0,
  });

  useEffect(() => { 
    loadBanners();
    loadArticles();
    loadCategories();
  }, []);

  const loadBanners = async () => {
    try {
      const res = await getBanners({ active_only: false });
      setBanners(res.data);
    } catch (error) {
      console.error("Error loading banners:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadArticles = async () => {
    try {
      const res = await getAdminArticles();
      setArticles(res.data.filter(a => a.status === "published").slice(0, 50));
    } catch (error) {
      console.error("Error loading articles:", error);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res.data);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      banner_type: "partner",
      position: "sidebar",
      image_url: "",
      image_base64: "",
      link_url: "",
      alt_text: "",
      is_active: true,
      order: 0,
      targeting_type: "all",
      target_categories: [],
      target_article_ids: [],
      visibility: "all",
      start_date: "",
      end_date: "",
      clicks: 0,
      impressions: 0,
    });
    setEditingBanner(null);
    setPreviewImage(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setPreviewImage(reader.result);
        setForm(prev => ({ ...prev, image_base64: base64, image_url: "" }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setForm({
      name: banner.name || "",
      banner_type: banner.banner_type || "partner",
      position: banner.position || "sidebar",
      image_url: banner.image_url || "",
      image_base64: "",
      link_url: banner.link_url || "",
      alt_text: banner.alt_text || "",
      is_active: banner.is_active !== false,
      order: banner.order || 0,
      targeting_type: banner.targeting_type || "all",
      target_categories: banner.target_categories || [],
      target_article_ids: banner.target_article_ids || [],
      visibility: banner.visibility || "all",
      start_date: banner.start_date || "",
      end_date: banner.end_date || "",
      clicks: banner.clicks || 0,
      impressions: banner.impressions || 0,
    });
    if (banner.image_base64) {
      setPreviewImage(`data:image/jpeg;base64,${banner.image_base64}`);
    } else if (banner.image_url) {
      setPreviewImage(banner.image_url);
    }
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Le nom est requis");
      return;
    }
    if (!form.image_base64 && !form.image_url && !editingBanner?.image_base64) {
      toast.error("Veuillez ajouter une image");
      return;
    }

    setSaving(true);
    try {
      const data = { ...form };
      // Clean up empty arrays
      if (data.target_categories.length === 0) delete data.target_categories;
      if (data.target_article_ids.length === 0) delete data.target_article_ids;

      if (editingBanner) {
        await updateBanner(editingBanner.id, data);
        toast.success("Bannière mise à jour !");
      } else {
        await createBanner(data);
        toast.success("Bannière créée !");
      }

      setShowForm(false);
      resetForm();
      loadBanners();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette bannière ?")) return;
    try {
      await deleteBanner(id);
      toast.success("Bannière supprimée");
      loadBanners();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const toggleActive = async (banner) => {
    try {
      await updateBanner(banner.id, { ...banner, is_active: !banner.is_active });
      toast.success(banner.is_active ? "Bannière désactivée" : "Bannière activée");
      loadBanners();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const getBannerStatus = (banner) => {
    if (banner.end_date && new Date(banner.end_date) < new Date()) return "expired";
    if (banner.start_date && new Date(banner.start_date) > new Date()) return "scheduled";
    return banner.is_active ? "active" : "inactive";
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-100 text-green-700",
      inactive: "bg-gray-100 text-gray-600",
      expired: "bg-red-100 text-red-700",
      scheduled: "bg-blue-100 text-blue-700",
    };
    const labels = { active: "Active", inactive: "Inactive", expired: "Expirée", scheduled: "Programmée" };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getPositionLabel = (position) => {
    return positionOptions.find(p => p.value === position)?.label || position;
  };

  const getTypeLabel = (type) => {
    return bannerTypes.find(t => t.value === type)?.label || type;
  };

  // Filter banners
  const filteredBanners = banners.filter(banner => {
    const status = getBannerStatus(banner);
    if (filterPosition !== "all" && banner.position !== filterPosition) return false;
    if (filterStatus !== "all") {
      if (filterStatus === "active" && status !== "active") return false;
      if (filterStatus === "inactive" && status !== "inactive") return false;
      if (filterStatus === "expired" && status !== "expired") return false;
    }
    return true;
  });

  return (
    <div data-testid="banners-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary flex items-center gap-3">
            <Megaphone className="h-8 w-8 text-accent" />
            Bannières Publicitaires
          </h1>
          <p className="text-muted-foreground mt-1">
            Gérez vos bannières avec ciblage précis
          </p>
        </div>
        <Button 
          className="bg-accent hover:bg-accent/90 gap-2" 
          onClick={() => { setShowForm(true); resetForm(); }}
        >
          <Plus className="h-4 w-4" />
          Nouvelle bannière
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-xl p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <Select value={filterPosition} onValueChange={setFilterPosition}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Emplacement" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les emplacements</SelectItem>
              {positionOptions.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="active">Actives</SelectItem>
              <SelectItem value="inactive">Inactives</SelectItem>
              <SelectItem value="expired">Expirées</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Banner Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-auto shadow-2xl">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl font-semibold">
                  {editingBanner ? "Modifier la bannière" : "Nouvelle bannière"}
                </h2>
                <Button variant="ghost" size="icon" onClick={() => { setShowForm(false); resetForm(); }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Nom de la bannière *</label>
                  <Input
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Ex: Promo été MSC"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Type de bannière</label>
                  <Select value={form.banner_type} onValueChange={v => setForm({ ...form, banner_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {bannerTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          <div>
                            <span className="font-medium">{t.label}</span>
                            <span className="text-xs text-muted-foreground ml-2">— {t.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Emplacement</label>
                  <Select value={form.position} onValueChange={v => setForm({ ...form, position: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {positionOptions.map(p => (
                        <SelectItem key={p.value} value={p.value}>
                          <div>
                            <span className="font-medium">{p.label}</span>
                            <span className="text-xs text-muted-foreground ml-2">— {p.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Image */}
              <div>
                <label className="block text-sm font-medium mb-2">Image de la bannière *</label>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-accent transition-colors">
                      <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">Cliquez ou glissez une image</span>
                      <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                    </label>
                  </div>
                  {previewImage && (
                    <div className="w-48 h-32 rounded-lg overflow-hidden border">
                      <img src={previewImage} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
                <div className="mt-2">
                  <Input
                    value={form.image_url}
                    onChange={e => { setForm({ ...form, image_url: e.target.value, image_base64: "" }); setPreviewImage(e.target.value); }}
                    placeholder="Ou collez une URL d'image"
                  />
                </div>
              </div>

              {/* Link & Alt */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Lien de destination</label>
                  <Input
                    value={form.link_url}
                    onChange={e => setForm({ ...form, link_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Texte alternatif</label>
                  <Input
                    value={form.alt_text}
                    onChange={e => setForm({ ...form, alt_text: e.target.value })}
                    placeholder="Description pour l'accessibilité"
                  />
                </div>
              </div>

              {/* Targeting Section */}
              {(form.position.includes("article") || form.position === "sidebar") && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Target className="h-4 w-4 text-accent" />
                    Ciblage
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Type de ciblage</label>
                      <Select value={form.targeting_type} onValueChange={v => setForm({ ...form, targeting_type: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {targetingOptions.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {form.targeting_type === "category" && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Catégories ciblées</label>
                        <div className="flex flex-wrap gap-2">
                          {categories.map(cat => (
                            <label 
                              key={cat.id} 
                              className={`px-3 py-1.5 rounded-full text-sm cursor-pointer border transition-colors ${
                                form.target_categories.includes(cat.id) 
                                  ? "bg-accent text-white border-accent" 
                                  : "bg-white border-gray-200 hover:border-accent"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={form.target_categories.includes(cat.id)}
                                onChange={() => {
                                  setForm(prev => ({
                                    ...prev,
                                    target_categories: prev.target_categories.includes(cat.id)
                                      ? prev.target_categories.filter(id => id !== cat.id)
                                      : [...prev.target_categories, cat.id]
                                  }));
                                }}
                                className="hidden"
                              />
                              {cat.name}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {form.targeting_type === "specific" && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Articles ciblés</label>
                        <div className="max-h-48 overflow-y-auto border rounded-lg bg-white">
                          {articles.map(article => (
                            <label 
                              key={article.id} 
                              className={`flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer border-b last:border-0 ${
                                form.target_article_ids.includes(article.id) ? "bg-accent/10" : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={form.target_article_ids.includes(article.id)}
                                onChange={() => {
                                  setForm(prev => ({
                                    ...prev,
                                    target_article_ids: prev.target_article_ids.includes(article.id)
                                      ? prev.target_article_ids.filter(id => id !== article.id)
                                      : [...prev.target_article_ids, article.id]
                                  }));
                                }}
                                className="rounded"
                              />
                              <span className="text-sm truncate">{article.title}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Visibility */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    Visibilité
                  </label>
                  <Select value={form.visibility} onValueChange={v => setForm({ ...form, visibility: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {visibilityOptions.map(v => (
                        <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Ordre d'affichage</label>
                  <Input
                    type="number"
                    value={form.order}
                    onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                    min={0}
                  />
                </div>
              </div>

              {/* Period */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date de début</label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={e => setForm({ ...form, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date de fin</label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={e => setForm({ ...form, end_date: e.target.value })}
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={v => setForm({ ...form, is_active: v })}
                />
                <div>
                  <p className="font-medium text-sm">Activer la bannière</p>
                  <p className="text-xs text-muted-foreground">Afficher immédiatement sur le site</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-accent hover:bg-accent/90" disabled={saving}>
                  {saving ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Enregistrement...</>
                  ) : (
                    <><Check className="h-4 w-4 mr-2" /> {editingBanner ? "Mettre à jour" : "Créer la bannière"}</>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Banners List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      ) : filteredBanners.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredBanners.map((banner) => {
            const status = getBannerStatus(banner);
            const isExpanded = expandedBanner === banner.id;
            
            return (
              <div 
                key={banner.id} 
                className={`bg-white border rounded-xl overflow-hidden transition-shadow hover:shadow-md ${
                  status === "expired" || status === "inactive" ? "opacity-70" : ""
                }`}
              >
                {/* Banner Preview */}
                <div className="h-32 bg-gray-100 relative">
                  {(banner.image_base64 || banner.image_url) && (
                    <img 
                      src={banner.image_base64 ? `data:image/jpeg;base64,${banner.image_base64}` : banner.image_url}
                      alt={banner.alt_text || banner.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute top-2 right-2 flex gap-1">
                    {getStatusBadge(status)}
                  </div>
                </div>
                
                {/* Banner Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h3 className="font-heading font-semibold text-primary truncate">{banner.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Layout className="h-3 w-3" />
                          {getPositionLabel(banner.position)}
                        </span>
                        <span>•</span>
                        <span>{getTypeLabel(banner.banner_type)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Stats */}
                  {(banner.impressions > 0 || banner.clicks > 0) && (
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2 mb-3">
                      <span className="flex items-center gap-1">
                        <Eye className="h-3 w-3" /> {banner.impressions || 0} vues
                      </span>
                      <span className="flex items-center gap-1">
                        <BarChart3 className="h-3 w-3" /> {banner.clicks || 0} clics
                      </span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-3 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(banner)}
                      className={banner.is_active ? "text-green-600" : "text-gray-500"}
                    >
                      {banner.is_active ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
                      {banner.is_active ? "Active" : "Inactive"}
                    </Button>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(banner)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(banner.id)} className="text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 bg-white border rounded-xl">
          <Megaphone className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-semibold text-primary mb-2">Aucune bannière</h3>
          <p className="text-muted-foreground mb-6">
            Créez votre première bannière publicitaire
          </p>
          <Button onClick={() => { setShowForm(true); resetForm(); }} className="bg-accent hover:bg-accent/90">
            <Plus className="h-4 w-4 mr-2" /> Créer une bannière
          </Button>
        </div>
      )}
    </div>
  );
}
